import {
  IconListItem,
  toWSMoneyString,
  WSButton,
  WSButtons,
  WSContainer,
  WSFlexBox,
  WSGridCentered,
  WSIcon,
  WSIconList,
  WSLoader,
  WSPanel,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { PreventLeave } from "../../../../components/PreventLeave/PreventLeave";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  QUERY_COLLABORATOR_DEDUCTIONS,
  QUERY_INVOICES,
  QUERY_PAYABLES
} from "../../../../query/payments/keys";
import { useCollaboratorsQuery } from "../../../../query/payments/queries";
import { getVisibleCollaboratorsSortByCreatedAt } from "../../../../query/payments/selectors";
import { flatfileService } from "../../../../services/flatfile";
import { paymentsService } from "../../../../services/payments";
import { track } from "../../../../utils/analytics";
import { bulkActionFactory } from "../../../../utils/bulkActionFactory";
import { pluralize } from "../../../../utils/stringHelper";
import { PayableStatus, UploadPayable } from "./BulkUploadPayablesInfo";
import {
  getLineItemsImportPreview,
  ImportPreviewStats,
  prepareLineItemsFromDeduction,
  prepareLineItemsFromPayable,
  ToBeCreatedDeduction,
  ToBeCreatedPayable,
  ToBeUpdatedPayable
} from "./bulkUploadPayablesUtils";

const payablesPluralizeOptions = {
  one: "payable",
  other: "payables"
};

const collaboratorsPluralizeOptions = {
  one: "collaborator",
  other: "collaborators"
};

const deductionsPluralizeOptions = {
  one: "deduction",
  other: "deductions"
};

export interface BulkUploadPayablesReviewProps {}

interface FailedPayable extends UploadPayable {
  error: string;
}
interface CreatePayableBulkActionFactoryResult {
  successItems: ToBeCreatedPayable[];
  failedItems: ToBeCreatedPayable[];
}

interface UpdatePayableBulkActionFactoryResult {
  successItems: ToBeUpdatedPayable[];
  failedItems: ToBeUpdatedPayable[];
}

interface CreateDeductionBulkActionFactoryResult {
  successItems: ToBeCreatedDeduction[];
  failedItems: ToBeCreatedDeduction[];
}

export const BulkUploadPayablesAndDeductionsReview: React.FC<BulkUploadPayablesReviewProps> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload payables");
  const history = useHistory<{
    lineItems: UploadPayable[];
    fileName: string;
    batchId: string;
  }>();
  const { fileName: uploadFileName, batchId } = history.location.state;

  const payableStatus: PayableStatus = qs.parse(
    history.location.search.substring(1)
  ).status as PayableStatus;

  const [importPreview, setImportPreview] = useState<ImportPreviewStats | null>(
    null
  );

  const [importFileName, setImportFileName] = useState("");

  const [
    bulkCreatePayablesApiResult,
    setBulkCreatePayablesApiResult
  ] = useState<CreatePayableBulkActionFactoryResult | null>(null);
  const [
    bulkUpdatePayablesApiResult,
    setBulkUpdatePayablesApiResult
  ] = useState<UpdatePayableBulkActionFactoryResult | null>(null);
  const [
    bulkCreateDeductionsApiResult,
    setBulkCreateDeductionsApiResult
  ] = useState<CreateDeductionBulkActionFactoryResult | null>(null);

  // below two state vars are for displaying the progress bar
  const [createPayablesApiResults, setCreatePayablesApiResults] = useState<
    {
      error?: any;
      email?: string;
      contractorId?: string;
      collaboratorId?: string;
    }[]
  >([]);
  const [updatePayablesApiResults, setUpdatePayablesApiResults] = useState<
    {
      error?: any;
      email?: string;
      contractorId?: string;
      collaboratorId?: string;
    }[]
  >([]);
  const [createDeductionApiResults, setCreateDeductionsApiResults] = useState<
    {
      error?: any;
      email?: string;
      contractorId?: string;
      collaboratorId?: string;
    }[]
  >([]);

  // for downloadable csv including errors
  const [failedLineItems, setFailedLineItems] = useState<FailedPayable[]>([]);

  const userId = useUserId();
  const collaboratorsQuery = useCollaboratorsQuery();
  const qFeatureFlags = useFeatureFlags();

  const existingCollaborators = getVisibleCollaboratorsSortByCreatedAt(
    collaboratorsQuery.data || []
  );

  const [confirmAndImport, confirmAndImportMeta] = useWSMutation<
    any,
    AxiosError,
    {
      toBeCreatedPayables: ToBeCreatedPayable[];
      toBeUpdatedPayables: ToBeUpdatedPayable[];
      toBeCreatedDeductions: ToBeCreatedDeduction[];
    }
  >(
    async ({
      toBeCreatedPayables,
      toBeUpdatedPayables,
      toBeCreatedDeductions
    }) => {
      const toBeCreatedPayablesApiResult = await bulkCreatePayables.run(
        toBeCreatedPayables
      );
      const toBeUpdatedPayablesApiResult = await bulkUpdatePayables.run(
        toBeUpdatedPayables
      );
      const toBeCreatedDeductionsApiResult = await bulkCreateDeductions.run(
        toBeCreatedDeductions
      );
      track("Payable Bulk Upload Completed", {
        createdCount: toBeCreatedPayablesApiResult.successItems.length,
        deductionsCreatedCount:
          toBeCreatedDeductionsApiResult.successItems.length,
        updatedCount: toBeUpdatedPayablesApiResult.successItems.length,
        errorCount:
          toBeCreatedPayablesApiResult.failedItems.length +
          toBeUpdatedPayablesApiResult.failedItems.length +
          toBeCreatedDeductionsApiResult.failedItems.length,
        errors: [
          ...[
            ...toBeCreatedPayablesApiResult.failedItems,
            ...toBeUpdatedPayablesApiResult.failedItems
          ].map(failedPayable => failedPayable.mergedPayable),
          ...[...toBeCreatedDeductionsApiResult.failedItems].map(
            failedDeduction => failedDeduction.csvDeduction
          )
        ]
      });
    },
    { dependencies: [QUERY_PAYABLES, QUERY_INVOICES] }
  );

  const [retry, retryMeta] = useWSMutation<
    any,
    AxiosError,
    {
      failedCreatePayables: ToBeCreatedPayable[];
      failedUpdatePayables: ToBeUpdatedPayable[];
      failedCreateDeductions: ToBeCreatedDeduction[];
    }
  >(
    async ({
      failedCreatePayables,
      failedUpdatePayables,
      failedCreateDeductions
    }) => {
      if (failedCreatePayables.length > 0) {
        await bulkCreatePayables.run(failedCreatePayables);
      }
      if (failedUpdatePayables.length > 0) {
        await bulkUpdatePayables.run(failedUpdatePayables);
      }
      if (failedCreateDeductions.length > 0) {
        await bulkCreateDeductions.run(failedCreateDeductions);
      }
    },
    {
      dependencies: [
        QUERY_PAYABLES,
        QUERY_INVOICES,
        QUERY_COLLABORATOR_DEDUCTIONS
      ]
    }
  );

  useEffect(() => {
    const getImportFileName = async () => {
      const response = await flatfileService.batch.get(batchId);
      setImportFileName(response.originalFile);
    };
    getImportFileName();
  });

  useEffect(() => {
    const getImportPreview = async () => {
      const lineItems = history.location.state?.lineItems || [];
      if (lineItems?.length === 0) {
        history.replace("/member/invoices/payables/bulk-upload/info");
        return;
      }
      const importPreviewStats = await getLineItemsImportPreview(
        lineItems,
        {
          userId,
          batchId,
          filename: uploadFileName ?? importFileName
        },
        existingCollaborators,
        payableStatus
      );
      setImportPreview(importPreviewStats);
      track("Payable Bulk Upload Previewed", {
        amount: toWSMoneyString(importPreviewStats?.totalLineItemsAmount),
        collaboratorCount: importPreviewStats?.collaboratorsCount,
        createdCount: importPreviewStats?.toBeCreatedPayables.length,
        updatedCount: importPreviewStats?.toBeUpdatedPayables.length,
        deductionsCreatedCount: importPreviewStats?.toBeCreatedDeductions.length
      });
    };
    getImportPreview();
  }, []);

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, []);

  const bulkCreatePayables = bulkActionFactory({
    action: (data: ToBeCreatedPayable) => createPayableWrapper(data),
    onFinish: result => {
      setBulkCreatePayablesApiResult(result);
    },
    concurrentLimit: 1 // API allows to create only 1 invoice/payable at time
  });

  const bulkUpdatePayables = bulkActionFactory({
    action: (data: ToBeUpdatedPayable) => updatePayableWrapper(data),
    onFinish: result => {
      setBulkUpdatePayablesApiResult(result);
    }
  });

  const bulkCreateDeductions = bulkActionFactory({
    action: (data: ToBeCreatedDeduction) => createDeductionWrapper(data),
    onFinish: result => {
      setBulkCreateDeductionsApiResult(result);
    }
  });

  const createPayableWrapper = async ({
    mergedPayable: uploadPayable,
    requestPayload
  }: ToBeCreatedPayable) => {
    const dueDate = new Date();
    let dateShift = 5 - dueDate.getDay();
    dateShift = dateShift < 0 ? dateShift + 7 : dateShift;
    dueDate.setDate(dueDate.getDate() + dateShift);

    try {
      const response = await paymentsService.payable.create(requestPayload);
      if (
        [PayableStatus.Open, PayableStatus.Approved].includes(payableStatus)
      ) {
        await paymentsService.payable.update(response.payableId, {
          client:
            payableStatus === PayableStatus.Approved
              ? {
                  workflowStatus: ClientWorkFlowStatus.Approved
                }
              : undefined,
          status: InvoiceStatus.Open
        });
      }
      setCreatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          })
        }
      ]);
    } catch (error) {
      setCreatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.collaboratorId && {
            collaboratorId: uploadPayable.collaboratorId
          }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          }),
          error: (error as AxiosError)?.response?.data.error
        }
      ]);
      const lineItems = prepareLineItemsFromPayable(uploadPayable);
      const lineItemsWithError = lineItems.map(li => ({
        ...li,
        error: (error as AxiosError)?.response?.data.error
      }));
      setFailedLineItems(failedLineItems => [
        ...failedLineItems,
        ...lineItemsWithError
      ]);
      throw error;
    }
  };

  const updatePayableWrapper = async ({
    mergedPayable: uploadPayable,
    requestPayload
  }: ToBeUpdatedPayable) => {
    try {
      await paymentsService.payable.update(requestPayload.id, requestPayload);
      setUpdatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          })
        }
      ]);
    } catch (error) {
      setUpdatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.collaboratorId && {
            collaboratorId: uploadPayable.collaboratorId
          }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          }),
          error: (error as AxiosError)?.response?.data.error
        }
      ]);
      const lineItems = prepareLineItemsFromPayable(uploadPayable);
      const lineItemsWithError = lineItems.map(li => ({
        ...li,
        error: (error as AxiosError)?.response?.data.error
      }));
      setFailedLineItems(failedLineItems => [
        ...failedLineItems,
        ...lineItemsWithError
      ]);
      throw error;
    }
  };

  const createDeductionWrapper = async ({
    csvDeduction,
    requestPayload
  }: ToBeCreatedDeduction) => {
    try {
      await paymentsService.collaboratorDeductions.create(requestPayload);
    } catch (error) {
      setCreateDeductionsApiResults(prev => [
        ...prev,
        {
          ...(csvDeduction.email && { email: csvDeduction.email }),
          ...(csvDeduction.collaboratorId && {
            collaboratorId: csvDeduction.collaboratorId
          }),
          ...(csvDeduction.contractorId && {
            contractorId: csvDeduction.contractorId
          }),
          error: (error as AxiosError)?.response?.data.error
        }
      ]);
      const lineItemWithError = {
        ...prepareLineItemsFromDeduction(csvDeduction),
        error: (error as AxiosError)?.response?.data.error
      };
      setFailedLineItems(failedLineItems => [
        ...failedLineItems,
        ...[lineItemWithError]
      ]);
      throw error;
    }
  };

  const onConfirmAndImport = async () => {
    setCreatePayablesApiResults([]);
    setUpdatePayablesApiResults([]);

    confirmAndImport({
      toBeCreatedPayables: importPreview?.toBeCreatedPayables || [],
      toBeUpdatedPayables: importPreview?.toBeUpdatedPayables || [],
      toBeCreatedDeductions: importPreview?.toBeCreatedDeductions || []
    });
  };

  const onRetry = async () => {
    const failedUploadingCreatePayablesApiResults = [
      ...(bulkCreatePayablesApiResult?.failedItems as ToBeCreatedPayable[])
    ];

    const failedUploadingUpdatePayablesApiResults = [
      ...(bulkUpdatePayablesApiResult?.failedItems as ToBeUpdatedPayable[])
    ];

    const failedUploadingDeductionsApiResults = [
      ...(bulkCreateDeductionsApiResult?.failedItems as ToBeCreatedDeduction[])
    ];

    if (failedUploadingCreatePayablesApiResults.length > 0) {
      setCreatePayablesApiResults([]);
      setBulkCreatePayablesApiResult(null);
    }

    if (failedUploadingUpdatePayablesApiResults.length > 0) {
      setUpdatePayablesApiResults([]);
      setBulkUpdatePayablesApiResult(null);
    }

    if (failedUploadingDeductionsApiResults.length > 0) {
      setCreateDeductionsApiResults([]);
      setBulkCreateDeductionsApiResult(null);
    }

    setFailedLineItems([]);

    const failedCreatePayables = bulkCreatePayablesApiResult?.failedItems || [];
    const failedUpdatePayables = bulkUpdatePayablesApiResult?.failedItems || [];
    const failedCreateDeductions =
      bulkCreateDeductionsApiResult?.failedItems || [];
    retry({
      failedCreatePayables,
      failedUpdatePayables,
      failedCreateDeductions
    });
  };

  const csvProps = {
    filename: `Wingspan_Failed_Bulk_Upload_Payables_${Date.now()}.csv`,
    headers: [
      { label: "Email", key: "email" },
      { label: "Collaborator ID", key: "collaboratorId" },
      { label: "External Contractor ID", key: "contractorId" },
      { label: "Amount (USD)", key: "amount" },
      { label: "Line Item Title", key: "lineItemTitle" },
      { label: "Line Item Description", key: "lineItemDescription" },
      { label: "Due Date", key: "dueDate" },
      { label: "Invoice Notes", key: "invoiceNotes" },
      { label: "Error", key: "error" }
    ],
    data: failedLineItems
  };

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 3}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            3 of 3
          </WSText.Label>
          <WSText.Heading2>Review & upload payables</WSText.Heading2>
          <WSPanel mt="L">
            {(function() {
              const totalUploadingPayables =
                (importPreview?.toBeCreatedPayables || []).length +
                (importPreview?.toBeUpdatedPayables || []).length;
              const totalUploadingDeductions = (
                importPreview?.toBeCreatedDeductions || []
              ).length;
              const totalUploadItems =
                totalUploadingPayables + totalUploadingDeductions;

              const totalLineItemsAmount = toWSMoneyString(
                (importPreview?.totalLineItemsAmount || 0) -
                  (importPreview?.totalDeductionsAmount || 0)
              );

              const totalPayableLineItems = toWSMoneyString(
                importPreview?.totalLineItemsAmount
              );

              const totalDeductionsAmount = toWSMoneyString(
                importPreview?.totalDeductionsAmount
              );

              const isUploading =
                confirmAndImportMeta.isLoading || retryMeta.isLoading;

              if (!importPreview) {
                return <WSLoader.Spinner />;
              } else if (isUploading) {
                const resultsLength =
                  createPayablesApiResults.length +
                  updatePayablesApiResults.length +
                  createDeductionApiResults.length;
                const importPercent = (resultsLength * 100) / totalUploadItems;
                return (
                  <>
                    <WSText.Heading3 mb="M">
                      Your information upload summary
                    </WSText.Heading3>
                    <PreventLeave
                      isEnabled={isUploading}
                      promptMessage="Your payables/deductions have already been uploaded and are in the process of being imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
                    />

                    <WSProgressBar
                      mt="L"
                      percent={importPercent || 0}
                      text={`Uploading: ${resultsLength} of ${totalUploadItems} items...`}
                    />
                  </>
                );
              } else if (
                (retryMeta.isSuccess || confirmAndImportMeta.isSuccess) &&
                bulkCreatePayablesApiResult &&
                bulkUpdatePayablesApiResult &&
                bulkCreateDeductionsApiResult
              ) {
                const failedUploadingPayablesApiResults = [
                  ...(bulkCreatePayablesApiResult?.failedItems as ToBeCreatedPayable[]),
                  ...(bulkUpdatePayablesApiResult?.failedItems as ToBeUpdatedPayable[])
                ];
                const failedUploadingDeductionsApiResults = [
                  ...(bulkCreateDeductionsApiResult?.failedItems as ToBeCreatedDeduction[])
                ];
                const failedUploads = [
                  ...failedUploadingPayablesApiResults,
                  ...failedUploadingDeductionsApiResults
                ];

                const successfulPayablesUploadCount =
                  bulkCreatePayablesApiResult?.successItems.length +
                  bulkUpdatePayablesApiResult?.successItems.length;
                const successfulDeductionsUploadCount =
                  bulkCreateDeductionsApiResult?.successItems.length;
                const successfulUploadedCount =
                  successfulPayablesUploadCount +
                  successfulDeductionsUploadCount;

                const renderUploadSuccessMessage = () => {
                  if (totalUploadItems === successfulUploadedCount) {
                    let message = "";
                    if (
                      successfulPayablesUploadCount > 0 &&
                      successfulDeductionsUploadCount > 0
                    ) {
                      message = `${successfulPayablesUploadCount} ${pluralize(
                        successfulPayablesUploadCount,
                        payablesPluralizeOptions
                      )} and ${successfulDeductionsUploadCount} ${pluralize(
                        successfulDeductionsUploadCount,
                        deductionsPluralizeOptions
                      )} were successfully uploaded.`;
                    } else if (successfulPayablesUploadCount > 0) {
                      message = `${successfulPayablesUploadCount} ${pluralize(
                        successfulPayablesUploadCount,
                        payablesPluralizeOptions
                      )} were successfully uploaded.`;
                    } else if (successfulDeductionsUploadCount > 0) {
                      message = `${successfulDeductionsUploadCount} ${pluralize(
                        successfulDeductionsUploadCount,
                        deductionsPluralizeOptions
                      )} were successfully uploaded.`;
                    }
                    return <WSText>{message}</WSText>;
                  }
                  return null;
                };

                return (
                  <>
                    <WSText.Heading3 mb="M">
                      Your information upload summary
                    </WSText.Heading3>
                    <WSFlexBox.CenterY mb="M">
                      {failedUploads.length > 0 ? (
                        <WSFlexBox direction="column">
                          {failedUploadingPayablesApiResults.length > 0 ? (
                            <WSFlexBox mb="XS" alignItems="center">
                              <WSIcon name="warning" color="coral" mr="S" />
                              <WSText>
                                {failedUploadingPayablesApiResults.length}/
                                {totalUploadingPayables} payables failed to
                                upload.{" "}
                              </WSText>
                            </WSFlexBox>
                          ) : (
                            <WSFlexBox mb="XS" alignItems="center">
                              <WSIcon
                                name="successMark"
                                size="M"
                                color="green500"
                                mr="S"
                              />
                              <WSText>
                                {successfulPayablesUploadCount}{" "}
                                {pluralize(
                                  successfulPayablesUploadCount,
                                  payablesPluralizeOptions
                                )}{" "}
                                successfully uploaded.
                              </WSText>
                            </WSFlexBox>
                          )}
                          {failedUploadingDeductionsApiResults.length > 0 ? (
                            <WSFlexBox mb="XS" alignItems="center">
                              <WSIcon name="warning" color="coral" mr="S" />
                              <WSText mt="XS">
                                {failedUploadingDeductionsApiResults.length}/
                                {totalUploadingDeductions} deductions failed to
                                upload.{" "}
                              </WSText>
                            </WSFlexBox>
                          ) : (
                            <WSFlexBox mb="XS" alignItems="center">
                              <WSIcon
                                name="successMark"
                                size="M"
                                color="green500"
                                mr="S"
                              />
                              <WSText mt="XS">
                                {successfulDeductionsUploadCount}{" "}
                                {pluralize(
                                  successfulDeductionsUploadCount,
                                  deductionsPluralizeOptions
                                )}{" "}
                                successfully uploaded.
                              </WSText>
                            </WSFlexBox>
                          )}
                          <CSVLink {...csvProps}>
                            <WSButton.Link mt="M">
                              Download CSV including errors.
                            </WSButton.Link>
                          </CSVLink>
                        </WSFlexBox>
                      ) : (
                        renderUploadSuccessMessage()
                      )}
                    </WSFlexBox.CenterY>
                    <WSButton.Link
                      mb="L"
                      onClick={() => {
                        history.push(
                          "/member/invoices/payables/bulk-upload/info"
                        );
                      }}
                    >
                      Restart upload payables
                    </WSButton.Link>
                    {failedUploadingPayablesApiResults.length > 0 ? (
                      <WSButtons forceFullWidth>
                        <WSButton fullWidth onClick={onRetry}>
                          Try again
                        </WSButton>
                        <WSButton.Secondary
                          fullWidth
                          onClick={() =>
                            history.push("/member/invoices/payables")
                          }
                        >
                          Go to payables
                        </WSButton.Secondary>
                      </WSButtons>
                    ) : (
                      <WSButton
                        fullWidth
                        onClick={() =>
                          history.push("/member/invoices/payables")
                        }
                      >
                        Go to payables
                      </WSButton>
                    )}
                  </>
                );
              }

              return (
                <>
                  <WSText.Heading3 mb="M">Import Preview</WSText.Heading3>
                  <WSIconList
                    mb="L"
                    items={[
                      ...(importPreview?.totalLineItemsAmount > 0
                        ? [
                            {
                              description: `${totalPayableLineItems} of payables to ${
                                importPreview.collaboratorsCount
                              } ${pluralize(
                                importPreview.collaboratorsCount,
                                collaboratorsPluralizeOptions
                              )} will be imported.`,
                              icon: {
                                name: "coin",
                                color: "blue500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : []),
                      ...(importPreview.toBeCreatedPayables.length > 0
                        ? [
                            {
                              description: `${
                                importPreview.toBeCreatedPayables.length
                              } new ${payableStatus.toLowerCase()} ${pluralize(
                                importPreview.toBeCreatedPayables.length,
                                payablesPluralizeOptions
                              )} will be created`,
                              icon: {
                                name: "receipt",
                                color: "blue500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : []),
                      ...(importPreview.toBeUpdatedPayables.length > 0
                        ? [
                            {
                              description: `${
                                importPreview.toBeUpdatedPayables.length
                              } existing ${pluralize(
                                importPreview.toBeUpdatedPayables.length,
                                payablesPluralizeOptions
                              )} will be updated with imported line items.`,
                              icon: {
                                name: "fileEdit",
                                color: "blue500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : []),
                      ...(importPreview.deductionsCollaborators > 0
                        ? [
                            {
                              description: `${totalDeductionsAmount} of deductions for ${
                                importPreview.deductionsCollaborators
                              } ${pluralize(
                                importPreview.deductionsCollaborators,
                                collaboratorsPluralizeOptions
                              )} will be imported`,
                              icon: {
                                name: "circleMinus",
                                color: "blue500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : []),
                      {
                        description: `Net import amount is ${totalLineItemsAmount}`,
                        icon: {
                          name: "addFunds",
                          color: "blue500",
                          size: "M"
                        }
                      }
                    ]}
                  />

                  <WSButton
                    icon="upload"
                    fullWidth
                    onClick={onConfirmAndImport}
                    loading={confirmAndImportMeta.isLoading}
                  >
                    Import Payables
                  </WSButton>
                </>
              );
            })()}
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
